import React, { useEffect, useMemo, useState } from "react";
import styles from "./Satisfy.module.scss";
import SvgLike from "@/app/icons/SvgLikeIcon.svg";
import SvgDisLike from "@/app/icons/SvgDisLike.svg";
import SvgCloseIcon from "@/app/icons/SvgCloseIcon.svg";
import Feedback from "../aiFeedback";
import { ReportParentContentEnum } from "@/app/types/typeConfig/report";
import { IMConfigTypeEnum } from "@/app/types/typeConfig/chatQuestion";
import { Popup } from "antd-mobile";
import { isMobileDevice } from "@/app/utils/device";
import { useTranslations } from "@/app/locales";
import {
  Feedback as FeedbackType,
  isDislikeFeedbackPayload,
  isLikeFeedbackPayload,
} from "@/app/store/types";

type CompletedRating = "none" | "like" | "dislike";

function completedFromFeedback(feedback?: FeedbackType): CompletedRating {
  if (!feedback) return "none";
  if (isLikeFeedbackPayload(feedback)) return "like";
  if (isDislikeFeedbackPayload(feedback)) return "dislike";
  return "none";
}

export default function Satisfy({
  feedback,
  streamingId,
  requestId,
  parentContentInfo,
  configType,
  handleLike,
  handleDislike,
}: {
  feedback?: FeedbackType;
  streamingId?: string;
  requestId?: string;
  parentContentInfo?: {
    parentContentId: string;
    parentContent: string;
    parentContentType: ReportParentContentEnum;
  };
  configType: IMConfigTypeEnum;
  handleLike: () => void;
  handleDislike: () => void;
}) {
  const [completed, setCompleted] = useState<CompletedRating>(() =>
    completedFromFeedback(feedback),
  );
  /** 点了「未解决」，正在走反馈流程（未提交不算评价完成） */
  const [dislikeFlowOpen, setDislikeFlowOpen] = useState(false);
  const [dislikePopupVisible, setDislikePopupVisible] = useState(false);
  const t = useTranslations();

  useEffect(() => {
    const next = completedFromFeedback(feedback);
    if (next !== "none") setCompleted(next);
  }, [feedback]);

  const dislikeReadonlyInitial = useMemo(() => {
    if (!feedback || !isDislikeFeedbackPayload(feedback)) return null;
    return {
      feedbackType: feedback.feedbackType,
      feedback: feedback.feedbackContent ?? "",
      knowledgeId: feedback.knowledgeId,
    };
  }, [feedback]);

  /** 仅：已解决点击后，或未解决且 Feedback 提交成功后 */
  const handleLikeClick = () => {
    if (completed !== "none") return;
    setDislikeFlowOpen(false);
    setDislikePopupVisible(false);
    setCompleted("like");
    handleLike();
  };

  const handleDislikeClick = () => {
    if (completed !== "none") return;
    setDislikeFlowOpen(true);
    if (isMobileDevice()) {
      setDislikePopupVisible(true);
    }
  };

  const handleFeedbackSubmitted = () => {
    setCompleted("dislike");
    setDislikeFlowOpen(false);
    setDislikePopupVisible(false);
    handleDislike();
  };

  const handleCloseDislikePopup = () => {
    setDislikePopupVisible(false);
    setDislikeFlowOpen(false);
  };

  return (
    <>
      <div className={styles.satisfy}>
        <div className={styles.title}>{t.aiFeedback.satisfyTitle}</div>
        <div
          className={`${styles.options} ${completed !== "none" ? styles.commented : ""}`}
        >
          <div
            className={`${styles.option} ${completed === "dislike" ? styles.active : ""}`}
            onClick={handleDislikeClick}
          >
            <SvgDisLike className={`${styles.icon} ${styles.dislike}`} />
            <span>{t.aiFeedback.satisfyDislike}</span>
          </div>
          <div
            className={`${styles.option} ${completed === "like" ? styles.active : ""}`}
            onClick={handleLikeClick}
          >
            <SvgLike className={`${styles.icon} ${styles.like}`} />
            <span>{t.aiFeedback.satisfyLike}</span>
          </div>
        </div>
      </div>
      {completed === "like" && (
        <div className={styles.satisfyLiked}>
          <span>{t.aiFeedback.satisfyLiked}</span>
        </div>
      )}
      {completed === "dislike" && dislikeReadonlyInitial && (
        <div className={styles.disSatisfyReadonly}>
          <Feedback
            readOnly
            readonlyInitial={dislikeReadonlyInitial}
            streamingId={streamingId}
            templateProps={{
              likeOrDislike: "dislike",
              requestId,
            }}
            type={isMobileDevice() ? "button" : "default"}
            fromClassName={isMobileDevice() ? "buttonStyle" : undefined}
            parentContentInfo={parentContentInfo}
            configType={configType}
          />
        </div>
      )}
      {completed === "dislike" && !dislikeReadonlyInitial && (
        <div className={styles.satisfyDislikedShort}>
          <span>{t.aiFeedback.thanksFeedback}</span>
        </div>
      )}
      {dislikeFlowOpen &&
        completed === "none" &&
        (isMobileDevice() ? (
          <Popup
            closeIcon={<SvgCloseIcon onClick={handleCloseDislikePopup} />}
            showCloseButton
            visible={dislikePopupVisible}
            onClose={handleCloseDislikePopup}
            destroyOnClose={true}
            className={styles.disSatisfyPopup}
            style={{ zIndex: 2001, borderRadius: "10px" }}
          >
            <div className={styles.disSatisfy}>
              <Feedback
                streamingId={streamingId}
                onSubmit={handleFeedbackSubmitted}
                fromClassName="buttonStyle"
                templateProps={{
                  likeOrDislike: "dislike",
                  requestId,
                }}
                type="button"
                parentContentInfo={parentContentInfo}
                configType={configType}
              />
            </div>
          </Popup>
        ) : (
          <div className={styles.disSatisfy}>
            <Feedback
              streamingId={streamingId}
              onSubmit={handleFeedbackSubmitted}
              templateProps={{
                likeOrDislike: "dislike",
                requestId,
              }}
              parentContentInfo={parentContentInfo}
              configType={configType}
            />
          </div>
        ))}
    </>
  );
}
